import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';

interface Line { itemId: string | null; itemNameSnapshot: string; quantity: number; unitPrice: number; lineDiscount: number }
interface Quotation { quotationId: string; number: string; customerNameSnapshot: string; status: number; total: number; convertedSalesOrderId: string | null }
interface SoLine { salesOrderLineId: string; itemNameSnapshot: string; quantity: number; deliveredQuantity: number }
interface SalesOrder { salesOrderId: string; number: string; customerNameSnapshot: string; status: number; deliveryState: number; billingState: number; total: number; lines: SoLine[] }
interface BranchDto { branchId: string; nameAr: string }

const QUO_STATUS: Record<number, string> = { 1: 'draft', 2: 'sent', 3: 'accepted', 4: 'lost', 5: 'expired' };
const SO_STATUS: Record<number, string> = { 1: 'draft', 2: 'confirmed', 3: 'completed', 4: 'cancelled' };
const DELIV: Record<number, string> = { 1: 'notDelivered', 2: 'partial', 3: 'delivered' };

function money(v: number) {
  // Arabic layout, Western digits (Palestinian convention — CLAUDE.md §2).
  return new Intl.NumberFormat('ar-PS', { style: 'currency', currency: 'ILS', minimumFractionDigits: 2, numberingSystem: 'latn' }).format(v);
}

export function SellingPage() {
  const { t } = useTranslation();
  return (
    <Stack gap="lg">
      <PageHeader eyebrow={t('nav.sections.operations')} title={t('selling.title')} subtitle={t('selling.subtitle')} />
      <Tabs defaultValue="quotations">
        <Tabs.List mb="md">
          <Tabs.Tab value="quotations">{t('selling.quotations')}</Tabs.Tab>
          <Tabs.Tab value="orders">{t('selling.orders')}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="quotations"><QuotationsTab /></Tabs.Panel>
        <Tabs.Panel value="orders"><OrdersTab /></Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function useBranches() {
  return useQuery({ queryKey: ['branches'] as const, queryFn: async () => (await apiClient.get<BranchDto[]>('/api/branches')).data });
}

function QuotationsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const { data: branches } = useBranches();
  const { data, isLoading } = useQuery({
    queryKey: ['quotations'] as const,
    queryFn: async () => (await apiClient.get<Quotation[]>('/api/selling/quotations')).data,
  });

  const convert = useMutation({
    mutationFn: async (q: Quotation) =>
      apiClient.post(`/api/selling/quotations/${q.quotationId}/convert?branchId=${branches![0]!.branchId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['quotations'] });
      void qc.invalidateQueries({ queryKey: ['sales-orders'] });
      notifications.show({ color: 'green', title: t('selling.converted'), message: '' });
    },
    onError: () => notifications.show({ color: 'red', title: t('common.toasts.errorTitle'), message: '' }),
  });

  return (
    <Stack gap="md">
      <Group justify="flex-end">
        <Button color="wheels" leftSection={<IconPlus size={16} stroke={1.8} />} onClick={() => setCreateOpen(true)}>
          {t('selling.newQuotation')}
        </Button>
      </Group>
      <Paper p="md">
        {isLoading ? <Group justify="center" py="lg"><Loader color="wheelsRed" /></Group>
          : (data ?? []).length === 0 ? <Text size="sm" c="dimmed" ta="center" py="lg">{t('selling.emptyQuotations')}</Text>
          : (
          <Table verticalSpacing="sm">
            <Table.Thead><Table.Tr>
              <Table.Th>{t('selling.number')}</Table.Th><Table.Th>{t('selling.customer')}</Table.Th>
              <Table.Th>{t('selling.total')}</Table.Th><Table.Th>{t('selling.statusCol')}</Table.Th><Table.Th />
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {(data ?? []).map((q) => (
                <Table.Tr key={q.quotationId}>
                  <Table.Td dir="ltr" className="font-mono">{q.number}</Table.Td>
                  <Table.Td>{q.customerNameSnapshot}</Table.Td>
                  <Table.Td className="font-numeric">{money(q.total)}</Table.Td>
                  <Table.Td><Badge variant="light">{t(`selling.quoStatus.${QUO_STATUS[q.status]}`)}</Badge></Table.Td>
                  <Table.Td><Group justify="flex-end">
                    {!q.convertedSalesOrderId && (
                      <Button size="xs" color="green" loading={convert.isPending}
                        disabled={!branches?.length} onClick={() => convert.mutate(q)}>
                        {t('selling.convert')}
                      </Button>
                    )}
                  </Group></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
      <DocModal opened={createOpen} onClose={() => setCreateOpen(false)} mode="quotation"
        onSaved={() => void qc.invalidateQueries({ queryKey: ['quotations'] })} />
    </Stack>
  );
}

function OrdersTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [deliverFor, setDeliverFor] = useState<SalesOrder | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['sales-orders'] as const,
    queryFn: async () => (await apiClient.get<SalesOrder[]>('/api/selling/orders')).data,
  });
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['sales-orders'] });

  const confirm = useMutation({
    mutationFn: async (o: SalesOrder) => apiClient.post(`/api/selling/orders/${o.salesOrderId}/confirm`),
    onSuccess: invalidate,
  });
  const bill = useMutation({
    mutationFn: async (o: SalesOrder) => apiClient.put(`/api/selling/orders/${o.salesOrderId}/billing`, { billingState: 2 }),
    onSuccess: invalidate,
  });

  return (
    <Stack gap="md">
      <Group justify="flex-end">
        <Button color="wheels" leftSection={<IconPlus size={16} stroke={1.8} />} onClick={() => setCreateOpen(true)}>
          {t('selling.newOrder')}
        </Button>
      </Group>
      <Paper p="md">
        {isLoading ? <Group justify="center" py="lg"><Loader color="wheelsRed" /></Group>
          : (data ?? []).length === 0 ? <Text size="sm" c="dimmed" ta="center" py="lg">{t('selling.emptyOrders')}</Text>
          : (
          <Table verticalSpacing="sm">
            <Table.Thead><Table.Tr>
              <Table.Th>{t('selling.number')}</Table.Th><Table.Th>{t('selling.customer')}</Table.Th>
              <Table.Th>{t('selling.total')}</Table.Th><Table.Th>{t('selling.statusCol')}</Table.Th>
              <Table.Th>{t('selling.delivery')}</Table.Th><Table.Th>{t('selling.billing')}</Table.Th><Table.Th />
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {(data ?? []).map((o) => (
                <Table.Tr key={o.salesOrderId}>
                  <Table.Td dir="ltr" className="font-mono">{o.number}</Table.Td>
                  <Table.Td>{o.customerNameSnapshot}</Table.Td>
                  <Table.Td className="font-numeric">{money(o.total)}</Table.Td>
                  <Table.Td><Badge variant="light">{t(`selling.soStatus.${SO_STATUS[o.status]}`)}</Badge></Table.Td>
                  <Table.Td><Badge variant="light" color={o.deliveryState === 3 ? 'green' : o.deliveryState === 2 ? 'orange' : 'gray'}>
                    {t(`selling.deliv.${DELIV[o.deliveryState]}`)}</Badge></Table.Td>
                  <Table.Td><Badge variant="light" color={o.billingState === 2 ? 'green' : 'gray'}>
                    {t(`selling.bill.${o.billingState === 2 ? 'billed' : 'unbilled'}`)}</Badge></Table.Td>
                  <Table.Td><Group gap="xs" justify="flex-end">
                    {o.status === 1 && <Button size="xs" variant="default" loading={confirm.isPending} onClick={() => confirm.mutate(o)}>{t('selling.confirm')}</Button>}
                    {o.status === 2 && o.deliveryState !== 3 && <Button size="xs" color="wheels" onClick={() => setDeliverFor(o)}>{t('selling.deliver')}</Button>}
                    {o.billingState === 1 && o.status !== 4 && <Button size="xs" variant="subtle" onClick={() => bill.mutate(o)}>{t('selling.markBilled')}</Button>}
                  </Group></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
      <DocModal opened={createOpen} onClose={() => setCreateOpen(false)} mode="order" onSaved={invalidate} />
      <DeliverModal order={deliverFor} onClose={() => setDeliverFor(null)} onSaved={invalidate} />
    </Stack>
  );
}

function DocModal({ opened, onClose, onSaved, mode }: {
  opened: boolean; onClose: () => void; onSaved: () => void; mode: 'quotation' | 'order';
}) {
  const { t } = useTranslation();
  const { data: branches } = useBranches();
  const [customer, setCustomer] = useState('');
  const [branchId, setBranchId] = useState<string | null>(null);
  const [lines, setLines] = useState<Line[]>([{ itemId: null, itemNameSnapshot: '', quantity: 1, unitPrice: 0, lineDiscount: 0 }]);

  const save = useMutation({
    mutationFn: async () => {
      const body = mode === 'quotation'
        ? { customerId: null, customerNameSnapshot: customer.trim(), quotationDate: today(), validUntil: null, notes: null, lines }
        : { branchId, customerId: null, customerNameSnapshot: customer.trim(), orderDate: today(), deliveryDate: null, notes: null, lines };
      return apiClient.post(mode === 'quotation' ? '/api/selling/quotations' : '/api/selling/orders', body);
    },
    onSuccess: () => { reset(); onSaved(); onClose(); },
    onError: (err) => notifications.show({ color: 'red', title: t('common.toasts.errorTitle'),
      message: (err as { response?: { data?: { title?: string } } }).response?.data?.title ?? '' }),
  });

  const reset = () => { setCustomer(''); setLines([{ itemId: null, itemNameSnapshot: '', quantity: 1, unitPrice: 0, lineDiscount: 0 }]); };
  const total = lines.reduce((s, l) => s + Math.max(0, l.quantity * l.unitPrice - l.lineDiscount), 0);
  const valid = customer.trim() && lines.every((l) => l.itemNameSnapshot.trim() && l.quantity > 0)
    && (mode === 'quotation' || !!branchId);

  return (
    <Modal opened={opened} onClose={onClose} size="xl"
      title={mode === 'quotation' ? t('selling.newQuotation') : t('selling.newOrder')}>
      <Stack gap="sm">
        <Group grow>
          <TextInput label={t('selling.customer')} value={customer} onChange={(e) => setCustomer(e.currentTarget.value)} withAsterisk />
          {mode === 'order' && (
            <Select label={t('selling.branch')} value={branchId} onChange={setBranchId} required
              data={(branches ?? []).map((b) => ({ value: b.branchId, label: b.nameAr }))} />
          )}
        </Group>
        <Table>
          <Table.Thead><Table.Tr>
            <Table.Th>{t('selling.item')}</Table.Th><Table.Th w={90}>{t('selling.qty')}</Table.Th>
            <Table.Th w={110}>{t('selling.price')}</Table.Th><Table.Th w={110}>{t('selling.discount')}</Table.Th><Table.Th w={40} />
          </Table.Tr></Table.Thead>
          <Table.Tbody>
            {lines.map((l, i) => (
              <Table.Tr key={i}>
                <Table.Td><TextInput value={l.itemNameSnapshot}
                  onChange={(e) => setLines((p) => p.map((x, j) => j === i ? { ...x, itemNameSnapshot: e.currentTarget.value } : x))} /></Table.Td>
                <Table.Td><NumberInput value={l.quantity} min={0} decimalScale={3} hideControls
                  onChange={(v) => setLines((p) => p.map((x, j) => j === i ? { ...x, quantity: Number(v) || 0 } : x))} /></Table.Td>
                <Table.Td><NumberInput value={l.unitPrice} min={0} decimalScale={2} hideControls
                  onChange={(v) => setLines((p) => p.map((x, j) => j === i ? { ...x, unitPrice: Number(v) || 0 } : x))} /></Table.Td>
                <Table.Td><NumberInput value={l.lineDiscount} min={0} decimalScale={2} hideControls
                  onChange={(v) => setLines((p) => p.map((x, j) => j === i ? { ...x, lineDiscount: Number(v) || 0 } : x))} /></Table.Td>
                <Table.Td><ActionIcon variant="subtle" color="red" disabled={lines.length === 1}
                  onClick={() => setLines((p) => p.filter((_, j) => j !== i))}><IconTrash size={16} /></ActionIcon></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Group justify="space-between">
          <Button size="xs" variant="default" leftSection={<IconPlus size={14} />}
            onClick={() => setLines((p) => [...p, { itemId: null, itemNameSnapshot: '', quantity: 1, unitPrice: 0, lineDiscount: 0 }])}>
            {t('selling.addLine')}
          </Button>
          <Text fw={600} className="font-numeric">{t('selling.total')}: {money(total)}</Text>
        </Group>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
          <Button color="wheels" loading={save.isPending} disabled={!valid} onClick={() => save.mutate()}>{t('common.save')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function DeliverModal({ order, onClose, onSaved }: { order: SalesOrder | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [qty, setQty] = useState<Record<string, number>>({});

  const deliver = useMutation({
    mutationFn: async () => {
      const lines = (order!.lines ?? [])
        .map((l) => ({ salesOrderLineId: l.salesOrderLineId, quantity: qty[l.salesOrderLineId] ?? (l.quantity - l.deliveredQuantity) }))
        .filter((l) => l.quantity > 0);
      return apiClient.post(`/api/selling/orders/${order!.salesOrderId}/deliveries`,
        { deliveryDate: today(), notes: null, lines });
    },
    onSuccess: () => { setQty({}); onSaved(); onClose(); },
    onError: (err) => notifications.show({ color: 'red', title: t('common.toasts.errorTitle'),
      message: (err as { response?: { data?: { title?: string } } }).response?.data?.title ?? '' }),
  });

  return (
    <Modal opened={order !== null} onClose={onClose} title={`${t('selling.deliver')} — ${order?.number ?? ''}`}>
      <Stack gap="sm">
        {(order?.lines ?? []).map((l) => {
          const remaining = l.quantity - l.deliveredQuantity;
          return (
            <Group key={l.salesOrderLineId} justify="space-between">
              <div>
                <Text size="sm">{l.itemNameSnapshot}</Text>
                <Text size="xs" c="dimmed" className="font-numeric">{t('selling.remaining')}: {remaining}</Text>
              </div>
              <NumberInput w={120} min={0} max={remaining} decimalScale={3} hideControls
                value={qty[l.salesOrderLineId] ?? remaining}
                onChange={(v) => setQty((p) => ({ ...p, [l.salesOrderLineId]: Number(v) || 0 }))} />
            </Group>
          );
        })}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
          <Button color="wheels" loading={deliver.isPending} onClick={() => deliver.mutate()}>{t('selling.deliver')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
